import type { PrivateActivityPackage } from '../../services/ttqService';

function isPlaceholderPapText(value: string): boolean {
  const text = value.trim();
  if (!text) return true;
  return text === '…' || /^\.{1,3}$/.test(text);
}

export type PapMatchedTag = {
  implicitAbilityTagId?: string;
  label: string;
  category?: string;
  reason?: string;
};

export type PapImplicitStep = {
  stepOrder?: number;
  implicitAbility: string;
  implicitAbilityIsNew?: boolean;
  actionObject?: string;
  reasoning?: string;
};

export type PapExamPointStep = {
  stepOrder?: number;
  examPointLabel: string;
  actionObject?: string;
  reasoning?: string;
};

export type PapDetail = {
  flowName?: string;
  flowSummary?: string;
  examPointSteps: PapExamPointStep[];
  matchedTags: PapMatchedTag[];
  implicitSteps: PapImplicitStep[];
  hasContent: boolean;
};

function readRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function readText(record: Record<string, unknown> | null | undefined, keys: string[]): string {
  if (!record) return '';
  for (const key of keys) {
    const v = record[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function readImplicitProjection(pap: PrivateActivityPackage | null | undefined): Record<string, unknown> {
  const p = pap?.implicitAbilityProjection;
  return readRecord(p) ?? {};
}

export function extractPapMatchedTags(pap: PrivateActivityPackage | null | undefined): PapMatchedTag[] {
  const projection = readImplicitProjection(pap);
  const raw = Array.isArray(projection.matchedTags)
    ? projection.matchedTags
    : Array.isArray(projection.tags)
      ? projection.tags
      : [];
  const out: PapMatchedTag[] = [];
  for (const item of raw) {
    const r = readRecord(item);
    if (!r) continue;
    const label = String(r.label || r.implicitAbility || '').trim();
    if (!label || isPlaceholderPapText(label)) continue;
    out.push({
      implicitAbilityTagId: String(r.implicitAbilityTagId || r.tagId || '').trim() || undefined,
      label,
      category: String(r.category || '').trim() || undefined,
      reason: String(r.reason || r.description || '').trim() || undefined,
    });
  }
  return out;
}

export function extractPapImplicitSteps(pap: PrivateActivityPackage | null | undefined): PapImplicitStep[] {
  const projection = readImplicitProjection(pap);
  const raw = Array.isArray(projection.steps) ? projection.steps : [];
  const out: PapImplicitStep[] = [];
  for (const item of raw) {
    const r = readRecord(item);
    if (!r) continue;
    const implicitAbility = readText(r, ['implicitAbility', 'label']);
    if (!implicitAbility || isPlaceholderPapText(implicitAbility)) continue;
    const actionObject = readText(r, ['actionObject', 'action', 'focus']);
    const reasoning = readText(r, ['reasoning', 'reason', 'explanation', 'description']);
    const stepOrder = typeof r.stepOrder === 'number'
      ? r.stepOrder
      : (typeof r.order === 'number' ? r.order : undefined);
    out.push({
      stepOrder,
      implicitAbility,
      implicitAbilityIsNew: r.implicitAbilityIsNew === true,
      actionObject: actionObject && !isPlaceholderPapText(actionObject) ? actionObject : undefined,
      reasoning: reasoning && !isPlaceholderPapText(reasoning) ? reasoning : undefined,
    });
  }
  return out;
}

function extractExamPointSteps(pap: PrivateActivityPackage | null | undefined): PapExamPointStep[] {
  const flow = readRecord(pap?.examPointFlowProjection) ?? {};
  const raw = Array.isArray(flow.steps) ? flow.steps : [];
  const out: PapExamPointStep[] = [];
  for (const item of raw) {
    const r = readRecord(item);
    if (!r) continue;
    const examPointLabel = readText(r, ['examPointLabel', 'label', 'title']);
    if (!examPointLabel) continue;
    out.push({
      stepOrder: typeof r.stepOrder === 'number' ? r.stepOrder : undefined,
      examPointLabel,
      actionObject: readText(r, ['actionObject', 'action', 'focus']) || undefined,
      reasoning: readText(r, ['reasoning', 'reason', 'explanation']) || undefined,
    });
  }
  return out;
}

export function buildPapDetail(pap: PrivateActivityPackage | null | undefined): PapDetail {
  const flow = readRecord(pap?.examPointFlowProjection) ?? {};
  const implicit = readImplicitProjection(pap);
  const examPointSteps = extractExamPointSteps(pap);
  const matchedTags = extractPapMatchedTags(pap);
  const implicitSteps = extractPapImplicitSteps(pap);
  const flowSummary = readText(flow, ['flowSummary', 'summary'])
    || readText(implicit, ['flowSummary', 'summary']);
  const flowName = readText(flow, ['flowName', 'name', 'title']);
  return {
    flowName: flowName || undefined,
    flowSummary: flowSummary || undefined,
    examPointSteps,
    matchedTags,
    implicitSteps,
    hasContent: examPointSteps.length > 0 || matchedTags.length > 0 || implicitSteps.length > 0 || Boolean(flowSummary),
  };
}

/**
 * 节点摘要：优先取隐性能力步骤的代表条目（如第 5 条），回退到 matchedTags / flowSummary。
 */
export function buildPapNodeSummary(pap: PrivateActivityPackage | null | undefined): string | null {
  const detail = buildPapDetail(pap);
  if (detail.implicitSteps.length > 0) {
    const labels = detail.implicitSteps.map((s) => s.implicitAbility).filter(Boolean);
    if (labels.length >= 5) return labels[4];
    if (labels.length > 0) return labels[labels.length - 1];
  }
  if (detail.matchedTags.length > 0) {
    return detail.matchedTags.map((t) => t.label).slice(0, 3).join(' · ');
  }
  if (detail.flowSummary) return detail.flowSummary;
  return null;
}

/** 鸟瞰六边形缩略：优先 PAP 考查点流程概要，其次节点摘要。 */
export function resolvePapOverviewLabel(pap: PrivateActivityPackage | null | undefined): string | null {
  const detail = buildPapDetail(pap);
  if (detail.flowSummary) return detail.flowSummary;
  if (detail.flowName) return detail.flowName;
  const nodeSummary = buildPapNodeSummary(pap);
  if (nodeSummary) return nodeSummary;
  if (detail.examPointSteps.length > 0) {
    return detail.examPointSteps
      .slice(0, 3)
      .map((step) => step.examPointLabel)
      .filter(Boolean)
      .join(' · ');
  }
  return null;
}

/** PAP 当前仅开放数学（与 Mobile2 一致）。 */
export function isParticlePapEligibleSubject(subject: string | null | undefined): boolean {
  const raw = String(subject ?? '').trim().toLowerCase();
  if (!raw) return false;
  return raw === 'math' || raw === 'mathematics' || raw === '数学';
}

function readStructuredMain(value: unknown): string {
  if (typeof value === 'string') {
    const text = value.trim();
    if ((text.startsWith('{') && text.endsWith('}')) || (text.startsWith('[') && text.endsWith(']'))) {
      try {
        return readStructuredMain(JSON.parse(text));
      } catch {
        return text;
      }
    }
    return text;
  }
  const record = readRecord(value);
  if (!record) return '';
  for (const key of ['main', 'Main', 'question', 'Question', 'text']) {
    const field = record[key];
    if (typeof field === 'string' && field.trim()) return field.trim();
  }
  return '';
}

const DICTATION_PARTICLE_TAG = '[听写稿]';

export function isParticlePapEligible(input: {
  subject?: string | null;
  questions?: unknown;
  answers?: unknown;
}): boolean {
  if (!isParticlePapEligibleSubject(input.subject)) return false;
  const questionMain = readStructuredMain(input.questions);
  const answerMain = readStructuredMain(input.answers);
  return !questionMain.startsWith(DICTATION_PARTICLE_TAG)
    && !answerMain.startsWith(DICTATION_PARTICLE_TAG);
}
